import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore } from '../stores/gameStore'
import { useSocket } from '../context/SocketContext'
import VictoryScreen from '../components/VictoryScreen'
import RoundSuccessScreen from '../components/RoundSuccessScreen'
import MatchRequestScreen from '../components/MatchRequestScreen'
import GameScreen from '../components/GameScreen'

const GamePage = () => {
  const navigate = useNavigate()
  const [selectedAnswers, setSelectedAnswers] = useState({})
  const [allAnswersSubmitted, setAllAnswersSubmitted] = useState(false)
  const [answersSubmittedToServer, setAnswersSubmittedToServer] = useState(false)
  
  const {
    player,
    gameState,
    setGameState,
    currentRound,
    lastMatchResult,
    continueState,
    matchRequestData,
    error,
    addAnswer,
    setContinueState,
    resetGame,
    clearError,
    setError,
  } = useGameStore()
  
  const { submitAnswer, continueToNextRound, requestPlayAgain, respondToPlayAgain, notifyPartnerLeft, startChat, requestChat, respondToChatRequest } = useSocket()

  useEffect(() => {
    // Redirect based on game state
    if (gameState === 'fallen' || gameState === 'try_again_request_sent' || gameState === 'try_again_request_received') {
      navigate('/fallen')
    } else if (gameState === 'chat') {
      // Navigate to chat page with chat ID from match request data
      if (matchRequestData && matchRequestData.chat_id) {
        navigate(`/chat/${matchRequestData.chat_id}`)
      } else {
        navigate('/chat/new') // Fallback
      }
    } else if (gameState !== 'in_game' && gameState !== 'success' && gameState !== 'victory' && gameState !== 'play_again_request_sent' && gameState !== 'play_again_request_received' && gameState !== 'chat_request_sent' && gameState !== 'chat_request_received' && gameState !== 'chat') {
      navigate('/lobby')
    }
  }, [gameState, navigate, matchRequestData])

  useEffect(() => {
    // Clear selected answers when starting a new round
    if (gameState === 'in_game' && currentRound.questions.length > 0) {
      setSelectedAnswers({})
      setAllAnswersSubmitted(false)
      setAnswersSubmittedToServer(false)
      // Scroll to top when new round starts
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentRound.level, gameState]) // Reset when level changes

  // Scroll to top when game state changes to different views
  useEffect(() => {
    if (gameState === 'in_game' || gameState === 'success' || 
        gameState === 'victory' || gameState === 'play_again_request_sent' || 
        gameState === 'play_again_request_received') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [gameState])

  useEffect(() => {
    // Check if all answers are submitted
    const answeredCount = Object.keys(selectedAnswers).length
    const allCompleted = answeredCount === currentRound.questions.length
    setAllAnswersSubmitted(allCompleted)
    
    // If all answers submitted, show "waiting for results" immediately
    if (allCompleted && answeredCount > 0) {
      console.log('All answers submitted locally, waiting for results...')
    }
  }, [selectedAnswers, currentRound.questions.length])

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    if (currentRound.timeRemaining <= 0 || answersSubmittedToServer) return
    
    const newSelectedAnswers = {
      ...selectedAnswers,
      [questionIndex]: answerIndex
    }
    setSelectedAnswers(newSelectedAnswers)
    
    // Store answer locally only - no server submission yet
    addAnswer(questionIndex, answerIndex)
  }

  const handleSubmitAnswers = () => {
    if (answersSubmittedToServer || !allAnswersSubmitted) return
    
    console.log('Submitting all answers to server...')
    setAnswersSubmittedToServer(true)
    
    // Submit all answers to server
    currentRound.questions.forEach((question, index) => {
      const answer = selectedAnswers[index]
      if (answer !== undefined) {
        submitAnswer(question.id, answer)
      }
    })
  }

  const handleContinueToNextRound = () => {
    if (currentRound.partnershipId && continueToNextRound) {
      // Mark that user has clicked continue
      setContinueState({
        userClicked: true,
        partnerWaiting: continueState.partnerWaiting,
        waitingForPartner: false,
      })
      continueToNextRound(currentRound.partnershipId)
    }
  }

  const handlePlayAgain = () => {
    // Request to play again with same partner
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      clearError()
      requestPlayAgain(lastMatchResult.partner_user_id)
    } else {
      setError('Unable to play again - partner information not available')
    }
  }

  const handleReturnToPurgatory = () => {
    // Notify partner that we're leaving (if we have partner info)
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      notifyPartnerLeft(lastMatchResult.partner_user_id)
    }
    
    // Set state to lobby and navigate
    setGameState('lobby')
    navigate('/lobby')
  }

  const handleAcceptPlayAgain = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToPlayAgain(matchRequestData.requester_id, true)
    }
  }

  const handleDeclinePlayAgain = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToPlayAgain(matchRequestData.requester_id, false)
    }
  }

  const handleStartChat = () => {
    // Request to chat with the partner from the last match
    if (lastMatchResult && lastMatchResult.partner_user_id) {
      requestChat(lastMatchResult.partner_user_id)
    } else {
      setError('Unable to request chat - partner information not available')
    }
  }

  const handleAcceptChatRequest = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToChatRequest(matchRequestData.requester_id, true)
    }
  }

  const handleDeclineChatRequest = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToChatRequest(matchRequestData.requester_id, false)
    }
  }

  // Show Chat Request states
  if (gameState === 'chat_request_sent' || gameState === 'chat_request_received') {
    return (
      <MatchRequestScreen
        gameState={gameState}
        matchRequestData={matchRequestData}
        onAccept={handleAcceptChatRequest}
        onDecline={handleDeclineChatRequest}
        onCancel={handleReturnToPurgatory}
      />
    )
  }

  // Show Play Again Request states
  if (gameState === 'play_again_request_sent' || gameState === 'play_again_request_received') {
    return (
      <MatchRequestScreen
        gameState={gameState}
        matchRequestData={matchRequestData}
        onAccept={handleAcceptPlayAgain}
        onDecline={handleDeclinePlayAgain}
        onCancel={handleReturnToPurgatory}
      />
    )
  }

  // Show results screen
  if (lastMatchResult && (gameState === 'success' || gameState === 'victory')) {
    if (gameState === 'victory') {
    return (
        <VictoryScreen
          lastMatchResult={lastMatchResult}
          onStartChat={handleStartChat}
          onReturnToPurgatory={handleReturnToPurgatory}
        />
      )
    }

    if (gameState === 'success') {
      return (
        <RoundSuccessScreen
          lastMatchResult={lastMatchResult}
          currentRound={currentRound}
          continueState={continueState}
          onContinueToNextRound={handleContinueToNextRound}
        />
      )
    }

  }

  // Main game screen
  return (
    <GameScreen
      player={player}
      currentRound={currentRound}
      selectedAnswers={selectedAnswers}
      allAnswersSubmitted={allAnswersSubmitted}
      answersSubmittedToServer={answersSubmittedToServer}
      onAnswerSelect={handleAnswerSelect}
      onSubmitAnswers={handleSubmitAnswers}
    />
  )
}

export default GamePage
